import React from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  useTheme,
  alpha,
} from "@mui/material";
import {
  TableRestaurant,
  TakeoutDining,
  RestaurantMenu,
  AccessTime,
  HourglassEmpty,
  Verified,
  CheckCircle,
  Done,
  TaskAlt,
  Cancel,
  ShoppingBag,
  StickyNote2,
  CheckCircleOutlined,
  Payments,
  HourglassTop,
  DoNotDisturb,
  Undo,
} from "@mui/icons-material";

const DashboardOrderCard = ({
  order,
  onClick,
  viewMode = "grid",
  enforceFixedHeight = true,
}) => {
  const theme = useTheme();
  const VISIBLE_ITEMS_LIMIT = 3;
  const isListView = viewMode === "list";

  const statusConfig = {
    pending: {
      color: "#f59e0b",
      dark: "#d97706",
      light: "#fef3c7",
      label: "Pending",
      icon: HourglassEmpty,
    },
    confirmed: {
      color: "#FF6600",
      dark: "#CC5200",
      light: "#FFF0E6",
      label: "Confirmed",
      icon: Verified,
    },
    preparing: {
      color: "#667eea",
      dark: "#764ba2",
      light: "#e0e7ff",
      label: "Preparing",
      icon: RestaurantMenu,
    },
    ready: {
      color: "#10b981",
      dark: "#059669",
      light: "#d1fae5",
      label: "Ready",
      icon: CheckCircle,
    },
    served: {
      color: "#6b7280",
      dark: "#4b5563",
      light: "#f3f4f6",
      label: "Served",
      icon: Done,
    },
    completed: {
      color: "#059669",
      dark: "#047857",
      light: "#d1fae5",
      label: "Completed",
      icon: TaskAlt,
    },
    cancelled: {
      color: "#ef4444",
      dark: "#dc2626",
      light: "#fee2e2",
      label: "Cancelled",
      icon: Cancel,
    },
  };

  const status = statusConfig[order.status] || statusConfig.pending;

  const paymentConfig = {
    completed: {
      label: "Paid",
      color: "#059669",
      bg: "#d1fae5",
      border: "#6ee7b7",
      icon: CheckCircleOutlined,
    },
    pending: {
      label: "Unpaid",
      color: "#d97706",
      bg: "#fef3c7",
      border: "#fcd34d",
      icon: HourglassTop,
    },
    partial: {
      label: "Partial",
      color: "#FF6600",
      bg: "#FFF0E6",
      border: "#FF8533",
      icon: Payments,
    },
    failed: {
      label: "Failed",
      color: "#dc2626",
      bg: "#fee2e2",
      border: "#fca5a5",
      icon: DoNotDisturb,
    },
    refunded: {
      label: "Refunded",
      color: "#7c3aed",
      bg: "#ede9fe",
      border: "#c4b5fd",
      icon: Undo,
    },
  };

  const payment = paymentConfig[order.paymentStatus] || paymentConfig.pending;
  const PaymentIcon = payment.icon;

  const isDineIn = order.orderType === "dine-in";

  // Extract table info
  const tableInfo = isDineIn
    ? order.Table?.tableNumber || order.table || "N/A"
    : "Takeaway";

  // Extract item count
  const itemCount = order.OrderItems?.length || order.items || 0;
  const visibleOrderItems = (order.OrderItems || []).slice(0, VISIBLE_ITEMS_LIMIT);
  const hiddenItemsCount = Math.max(itemCount - VISIBLE_ITEMS_LIMIT, 0);

  // Extract total amount
  const totalAmount = parseFloat(order.totalAmount || order.total || 0);

  // Calculate elapsed time for ongoing orders
  const getElapsedTime = () => {
    if (!order.createdAt) return null;

    const createdDate = new Date(order.createdAt);
    const now = new Date();

    // Check if order is from today
    const isToday = createdDate.toDateString() === now.toDateString();

    // Only show for pending, confirmed, preparing statuses
    const showElapsedStatuses = ["pending", "confirmed", "preparing"];
    if (!isToday || !showElapsedStatuses.includes(order.status)) {
      return null;
    }

    const diffMs = now - createdDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);

    let displayText;
    if (diffMins < 1) displayText = "Just now";
    else if (diffMins < 60)
      displayText = `${diffMins} min${diffMins !== 1 ? "s" : ""} ago`;
    else if (diffHours < 24)
      displayText = `${diffHours} hr${diffHours !== 1 ? "s" : ""} ago`;
    else return null;

    return { text: displayText, minutes: diffMins };
  };

  const elapsedTime = getElapsedTime();

  // Get color based on elapsed time
  const getElapsedTimeColor = () => {
    if (!elapsedTime) return null;

    if (elapsedTime.minutes < 15) {
      return { main: "#10b981", bg: "#d1fae5", border: "#059669" }; // Green
    } else if (elapsedTime.minutes <= 20) {
      return { main: "#f59e0b", bg: "#fef3c7", border: "#d97706" }; // Amber
    } else {
      return { main: "#ef4444", bg: "#fee2e2", border: "#dc2626" }; // Red
    }
  };

  const timeColor = getElapsedTimeColor();

  return (
    <Card
      elevation={0}
      sx={{
        cursor: "pointer",
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        borderRadius: "10px",
        border: "1px solid",
        borderColor: alpha(status.color, 0.2),
        bgcolor: "white",
        position: "relative",
        overflow: "hidden",
        width: "100%",
        height: enforceFixedHeight ? (isListView ? 240 : 300) : "auto",
        boxShadow: `0 1px 2px ${alpha("#000", 0.08)}`,
        "&:hover": {
          transform: "translateY(-1px)",
          boxShadow: `0 3px 10px ${alpha(status.color, 0.16)}`,
          borderColor: alpha(status.color, 0.4),
        },
        "&:active": {
          transform: "translateY(0px)",
        },
      }}
      onClick={onClick}
    >
      <CardContent
        sx={{
          p: 1.5,
          "&:last-child": { pb: 1.5 },
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Top Row: Order Number + Status */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1.25,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: status.color,
              fontSize: "1.15rem",
              letterSpacing: "-0.3px",
            }}
          >
            #
            {order.orderNumber
              ? order.orderNumber.split("-").pop()
              : `${order.id?.toString().slice(-4) || "N/A"}`}
          </Typography>

          <Chip
            icon={
              <status.icon
                sx={{
                  fontSize: "15px !important",
                  color: `${status.color} !important`,
                }}
              />
            }
            label={status.label}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: "0.68rem",
              height: 22,
              bgcolor: alpha(status.color, 0.12),
              color: status.color,
              border: `1px solid ${alpha(status.color, 0.25)}`,
              "& .MuiChip-label": {
                px: 0.8,
              },
              "& .MuiChip-icon": {
                ml: 0.5,
              },
            }}
          />
        </Box>

        {/* Middle Row: Table/Type + Time Badge */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1.25,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {isDineIn ? (
              <TableRestaurant
                sx={{
                  fontSize: 20,
                  color: theme.palette.primary.main,
                }}
              />
            ) : (
              <TakeoutDining
                sx={{
                  fontSize: 20,
                  color: theme.palette.primary.main,
                }}
              />
            )}
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 600,
                fontSize: "0.95rem",
              }}
            >
              {tableInfo}
            </Typography>
          </Box>

          {elapsedTime && timeColor && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 0.9,
                py: 0.35,
                borderRadius: "6px",
                bgcolor: timeColor.bg,
                border: `1px solid ${timeColor.border}`,
              }}
            >
              <AccessTime
                sx={{
                  fontSize: 12,
                  color: timeColor.main,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: timeColor.main,
                  fontWeight: 600,
                  fontSize: "0.64rem",
                  lineHeight: 1,
                }}
              >
                {elapsedTime.text}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Order Items Section */}
        {order.OrderItems && order.OrderItems.length > 0 && (
          <Box
            sx={{
              mb: 1.25,
              p: 1,
              borderRadius: "7px",
              bgcolor: alpha(theme.palette.grey[100], 0.5),
              border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
              minHeight: 86,
              maxHeight: 86,
              overflowY: "auto",
              overflowX: "hidden",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              "&::-webkit-scrollbar": {
                width: 0,
                height: 0,
                display: "none",
              },
            }}
          >
            {visibleOrderItems.map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: index < visibleOrderItems.length - 1 ? 0.35 : 0,
                  pb: index < visibleOrderItems.length - 1 ? 0.35 : 0,
                  borderBottom:
                    index < visibleOrderItems.length - 1
                      ? `1px solid ${alpha(theme.palette.divider, 0.15)}`
                      : "none",
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.78rem",
                      color: theme.palette.text.primary,
                      lineHeight: 1.25,
                      mb: 0.15,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {item.MenuItem?.name || "Item"}
                  </Typography>
                  {item.ItemVariation && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.68rem",
                        color: theme.palette.text.secondary,
                        fontWeight: 500,
                        display: "block",
                        mb: 0.2,
                      }}
                    >
                      {item.ItemVariation.name}
                    </Typography>
                  )}
                  {/* {item.OrderItemModifiers &&
                    item.OrderItemModifiers.length > 0 && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.7rem",
                          color: alpha(theme.palette.text.secondary, 0.85),
                          fontWeight: 400,
                          lineHeight: 1.3,
                          display: "block",
                        }}
                      >
                        +{" "}
                        {item.OrderItemModifiers.map(
                          (mod) => mod.ModifierOption?.name,
                        ).join(", ")}
                      </Typography>
                    )} */}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.74rem",
                      color: theme.palette.text.primary,
                      minWidth: "48px",
                      textAlign: "right",
                    }}
                  >
                    {item.quantity} × Rs.
                    {parseFloat(item.totalPrice).toFixed(0)}
                  </Typography>
                </Box>
              </Box>
            ))}
            {hiddenItemsCount > 0 && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.35,
                  color: theme.palette.text.secondary,
                  fontSize: "0.68rem",
                  fontWeight: 600,
                }}
              >
                +{hiddenItemsCount} more items
              </Typography>
            )}
          </Box>
        )}

        {/* Special Instructions */}
        {order.specialInstructions && (
          <Box
            sx={{
              mb: 1.25,
              display: "flex",
              alignItems: "flex-start",
              gap: 0.75,
              p: 1,
              borderRadius: "8px",
              bgcolor: alpha("#f59e0b", 0.08),
              border: `1px solid ${alpha("#f59e0b", 0.2)}`,
            }}
          >
            <StickyNote2
              sx={{ fontSize: 14, color: "#f59e0b", flexShrink: 0, mt: 0.15 }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: alpha(theme.palette.text.secondary, 0.75),
                  fontWeight: 600,
                  textTransform: "uppercase",
                  fontSize: "0.6rem",
                  letterSpacing: "0.6px",
                  display: "block",
                  mb: 0.25,
                }}
              >
                Special Instructions
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: "0.72rem",
                  lineHeight: 1.35,
                  fontStyle: "italic",
                  fontWeight: 500,
                }}
              >
                {order.specialInstructions}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Bottom Row: Amount + Items */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pt: 1.1,
            mt: "auto",
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          }}
        >
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "0.62rem",
                fontWeight: 500,
                display: "block",
                mb: 0.2,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Total Amount
            </Typography>
            <Typography
              variant="h4"
              sx={{
                color: status.color,
                fontWeight: 700,
                fontSize: "1.2rem",
                lineHeight: 1,
              }}
            >
              Rs.{totalAmount.toFixed(2)}
            </Typography>
          </Box>

          {/* Payment Status Badge */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              px: 1,
              py: 0.6,
              borderRadius: "8px",
              bgcolor: payment.bg,
              border: `1.5px solid ${payment.border}`,
              gap: 0.25,
            }}
          >
            <PaymentIcon sx={{ fontSize: 16, color: payment.color }} />
            <Typography
              sx={{
                fontSize: "0.58rem",
                fontWeight: 700,
                color: payment.color,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                lineHeight: 1,
              }}
            >
              {payment.label}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DashboardOrderCard;
